/**
 * HostedAuthShell — thin wrapper around upstream `<AuthAppShell>` that adds
 * the org-onboarding gate and the Flowlib brand.
 *
 * Upstream now ships:
 *   - SignInPage / SignUpPage with `socialProviders` + `signUpModes` and a
 *     `brand` slot.
 *   - EmailSentNotice + PasswordSetupPage (`/welcome/set-password` route).
 *   - 2FA verify page on the unauthenticated branch.
 *
 * Hosted's only delta is `<OnboardingGate>` (multi-tenant SaaS workspace
 * capture) and the brand mark.
 */

import type { ReactNode } from 'react';
import { AuthAppShell } from '@flowlib/user-auth/ui';
import { FlowlibLogo } from '@flowlib/ui';
import { OnboardingGate } from './OnboardingGate';
import { buildAuthApiBase } from './auth-api';
import { PastDueBanner } from '../billing/PastDueBanner';

export interface HostedAuthShellProps {
  children: ReactNode;
  apiBaseUrl: string;
  basePath: string;
}

const HOSTED_SOCIAL_PROVIDERS = ['google', 'github'] as const;

function FlowlibBrand() {
  return (
    <div className="flex items-center gap-3">
      <FlowlibLogo
        showLabel
        label="Flowlib"
        className="gap-4"
        iconClassName="h-12"
        labelClassName="text-fl-foreground text-3xl"
      />
      <span className="rounded-md border border-fl-primary/35 bg-fl-primary/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-fl-primary">
        Beta
      </span>
    </div>
  );
}

export function HostedAuthShell({ children, apiBaseUrl, basePath }: HostedAuthShellProps) {
  const authApiBase = buildAuthApiBase(apiBaseUrl);
  return (
    <AuthAppShell
      apiBaseUrl={apiBaseUrl}
      basePath={basePath}
      brand={<FlowlibBrand />}
      signInPageProps={{
        socialProviders: HOSTED_SOCIAL_PROVIDERS,
        socialDisclosure: 'always-visible',
      }}
      signUpPageProps={{
        signUpModes: ['magic-link'],
        socialProviders: HOSTED_SOCIAL_PROVIDERS,
        socialDisclosure: 'always-visible',
      }}
    >
      <OnboardingGate authApiBase={authApiBase}>
        <div className="flex min-h-screen flex-col">
          <PastDueBanner />
          <div className="flex min-h-0 flex-1 flex-col">{children}</div>
        </div>
      </OnboardingGate>
    </AuthAppShell>
  );
}
